import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { apiAuth } from "@/lib/api";
import { fieldClass } from "@/lib/validation";

function validate(current, next, confirm) {
  const errs = {};
  if (!current)            errs.current = "Aktuelles Passwort ist erforderlich.";
  if (!next)               errs.next    = "Neues Passwort ist erforderlich.";
  else if (next.length < 10) errs.next  = "Mindestens 10 Zeichen erforderlich.";
  if (!confirm)            errs.confirm = "Bestätigung ist erforderlich.";
  else if (next && confirm && next !== confirm) errs.confirm = "Passwörter stimmen nicht überein.";
  return errs;
}

export default function AdminForceChange() {
  const { user, setUserFromChangePassword } = useAuth();
  const navigate = useNavigate();
  const [current,  setCurrent]  = useState("");
  const [next,     setNext]     = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [touched,  setTouched]  = useState({ current: false, next: false, confirm: false });
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [loading,  setLoading]  = useState(false);

  if (!user) return null;

  const errs = validate(current, next, confirm);
  const show = (f) => (submitted || touched[f]) && !!errs[f];

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setApiError(null);
    if (Object.keys(errs).length) return;
    if (next === current) { setApiError("Neues Passwort darf nicht dem alten entsprechen."); return; }
    setLoading(true);
    try {
      await apiAuth.post("/auth/change-password", { current_password: current, new_password: next });
      setUserFromChangePassword({ must_change_password: false });
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const map = {
        current_password_wrong:    "Aktuelles Passwort stimmt nicht.",
        new_password_same_as_old:  "Bitte ein neues Passwort wählen.",
      };
      setApiError(map[detail] || "Etwas ist schiefgelaufen. Bitte versuche es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-porcelain px-5 py-12" data-testid="page-force-change">
      <div className="relative w-full max-w-md">
        <div aria-hidden="true" className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-skyMist/70 blur-3xl" />
        <div className="relative bg-white border border-sandBeige rounded-3xl shadow-soft p-8">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={18} className="text-raspberry" />
            <p className="text-xs text-warmGrey uppercase tracking-wide">Sicherheit</p>
          </div>
          <h1 className="font-serif text-3xl text-cocoa">Passwort jetzt ändern</h1>
          <p className="text-sm text-warmGrey mt-2">
            Du loggst dich zum ersten Mal ein. Bitte setze ein eigenes, sicheres Passwort (mind. 10 Zeichen).
          </p>

          <form onSubmit={onSubmit} noValidate className="mt-6 flex flex-col gap-4" data-testid="force-change-form">
            {/* Current password */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="fc-current" className="text-xs font-medium text-cocoa">
                Aktuelles Passwort <span className="text-raspberry">*</span>
              </label>
              <input
                id="fc-current"
                type="password"
                value={current}
                onChange={(e) => { setCurrent(e.target.value); setApiError(null); }}
                onBlur={() => setTouched((t) => ({ ...t, current: true }))}
                className={fieldClass("wit-input", show("current"), !!current && !errs.current)}
                data-testid="force-current"
              />
              {show("current") && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle size={12} />{errs.current}
                </p>
              )}
            </div>

            {/* New password */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="fc-new" className="text-xs font-medium text-cocoa">
                Neues Passwort <span className="text-raspberry">*</span>
              </label>
              <input
                id="fc-new"
                type="password"
                value={next}
                onChange={(e) => { setNext(e.target.value); setApiError(null); }}
                onBlur={() => setTouched((t) => ({ ...t, next: true }))}
                className={fieldClass("wit-input", show("next"), !!next && !errs.next)}
                data-testid="force-new"
              />
              {show("next") && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle size={12} />{errs.next}
                </p>
              )}
              {!show("next") && next && next.length < 10 && (
                <p className="text-xs text-warmGrey">{next.length}/10 Zeichen</p>
              )}
            </div>

            {/* Confirm */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="fc-confirm" className="text-xs font-medium text-cocoa">
                Neues Passwort bestätigen <span className="text-raspberry">*</span>
              </label>
              <input
                id="fc-confirm"
                type="password"
                value={confirm}
                onChange={(e) => { setConfirm(e.target.value); setApiError(null); }}
                onBlur={() => setTouched((t) => ({ ...t, confirm: true }))}
                className={fieldClass("wit-input", show("confirm"), !!confirm && !errs.confirm)}
                data-testid="force-confirm"
              />
              {show("confirm") && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle size={12} />{errs.confirm}
                </p>
              )}
            </div>

            {apiError && (
              <p className="text-sm text-coral flex items-center gap-2" role="alert" data-testid="force-error">
                <AlertCircle size={14} /> {apiError}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="wit-btn-primary disabled:opacity-60"
              data-testid="force-submit"
            >
              {loading ? "Wird gespeichert …" : <>Passwort speichern <ArrowRight size={16} /></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
